/**
 * Centralized Guardian Configuration
 * Single source of truth for all guardian data across the application
 */

// Import all guardian images
import steriImg from '@/assets/images/patty/guardian/steri.svg';
import handHygieneImg from '@/assets/images/patty/guardian/hand-hygenie.svg';
import environmentImg from '@/assets/images/patty/guardian/environment.svg';
import healthSafetyImg from '@/assets/images/patty/guardian/health-safety.svg';
import constructionImg from '@/assets/images/patty/guardian/construction.svg';
import dataAnalysisImg from '@/assets/images/patty/guardian/data-analysis.svg';
import educationImg from '@/assets/images/patty/guardian/education.svg';
import mifuImg from '@/assets/images/patty/guardian/mifu.svg';
import utiImg from '@/assets/images/patty/guardian/uti.svg';
import outbreakImg from '@/assets/images/patty/guardian/outbreak.svg';
import immunizationImg from '@/assets/images/patty/guardian/immunization.svg';
import pattyImg from '@/assets/images/patty/guardian/patty.svg';

// Import wave guardian images
import steriWaveImg from '@/assets/images/patty/guardian/ster-wave.svg';
import handHygieneWaveImg from '@/assets/images/patty/guardian/hand-hygenie-wave.svg';
import environmentWaveImg from '@/assets/images/patty/guardian/environment-wave.svg';
import healthSafetyWaveImg from '@/assets/images/patty/guardian/health-safety-wave.svg';
import constructionWaveImg from '@/assets/images/patty/guardian/construction-wave.svg';
import dataAnalysisWaveImg from '@/assets/images/patty/guardian/data-analysis-wave.svg';
import educationWaveImg from '@/assets/images/patty/guardian/education-wave.svg';
import mifuWaveImg from '@/assets/images/patty/guardian/mifu-wave.svg';
import utiWaveImg from '@/assets/images/patty/guardian/uti-wave.svg';
import outbreakWaveImg from '@/assets/images/patty/guardian/outbreak-wave.svg';
import immunizationWaveImg from '@/assets/images/patty/guardian/immunization-wave.svg';
import pattyWaveImg from '@/assets/images/patty/guardian/patty-wave.svg';

/**
 * Guardian definitions with all properties
 * @property {string} id - Unique identifier
 * @property {string} name - Guardian's name
 * @property {string} label - Display label/expertise area
 * @property {string} agentType - Backend agent type
 * @property {*} image - Imported image asset
 * @property {*} waveImage - Imported wave image asset for chat view
 * @property {string} imagePath - Relative path to image
 * @property {string} description - Short description for thread view
 * @property {string} shortDescription - Truncated description for cards
 * @property {string} fullDescription - Full detailed description
 * @property {string[]} keywords - Keywords for suggestion matching
 */
export const GUARDIANS = [
    {
        id: 'hand-hygiene',
        name: 'Finn',
        label: 'Hand Hygiene',
        agentType: 'HAND_HYGIENE',
        image: handHygieneImg,
        waveImage: handHygieneWaveImg,
        imagePath: 'patty/guardian/hand-hygenie.svg',
        description:
            'I can assist with any questions you have about Hand Hygiene.',
        shortDescription:
            'Supports best-practice hand hygiene programs with clear, actionable guidance.',
        fullDescription:
            'Supports best-practice hand hygiene programs with clear, actionable guidance. Finn provides instruction on proper technique, hand sanitizer use, glove protocols, and compliance monitoring to strengthen hand hygiene practices across the organization.',
        keywords: [
            'hand hygiene',
            'handwash',
            'hand wash',
            'sanitizer',
            'glove',
        ],
    },
    {
        id: 'environmental',
        name: 'Max',
        label: 'Environmental',
        agentType: 'ENVIRONMENTAL',
        image: environmentImg,
        waveImage: environmentWaveImg,
        imagePath: 'patty/guardian/environment.svg',
        description:
            'I can assist with any questions you have about Environmental cleaning and disinfection.',
        shortDescription:
            'Offers detailed guidance on environmental cleaning and disinfection best practices.',
        fullDescription:
            'Offers detailed guidance on environmental cleaning and disinfection best practices. Max supports surface disinfection protocols, cleaning frequencies, product selection, and environmental monitoring aligned with IPAC and regulatory expectations for safe care environments.',
        keywords: ['environment', 'cleaning', 'surface'],
    },
    {
        id: 'sterilization',
        name: 'Juno',
        label: 'Sterilization',
        agentType: 'STERILIZATION',
        image: steriImg,
        waveImage: steriWaveImg,
        imagePath: 'patty/guardian/steri.svg',
        description:
            'I can assist with any questions you have about Sterilization.',
        shortDescription:
            'Provides expert support for sterilization and reprocessing workflows.',
        fullDescription:
            'Provides expert support for sterilization and reprocessing workflows. Juno offers guidance on sterilization techniques, equipment maintenance, monitoring requirements, and applicable regulatory standards across steam, chemical, and low-temperature sterilization methods.',
        keywords: ['steril', 'autoclave', 'instrument', 'disinfection'],
    },
    {
        id: 'outbreak',
        name: 'Niko',
        label: 'Outbreak',
        agentType: 'OUTBREAK',
        image: outbreakImg,
        waveImage: outbreakWaveImg,
        imagePath: 'patty/guardian/outbreak.svg',
        description:
            'I can assist with any questions you have about Outbreak-related IPAC requirements.',
        shortDescription:
            'Supports outbreak preparedness, management, and response.',
        fullDescription:
            'Supports outbreak preparedness, management, and response. Niko provides guidance on outbreak identification, control measures, cohorting, communication protocols, reporting requirements, and documentation to support timely, compliant outbreak response.',
        keywords: [
            'outbreak',
            'epidemic',
            'cluster',
            'cohorting',
            'isolation',
            'containment',
            'reporting',
            'disease spread',
        ],
    },
    {
        id: 'health-safety',
        name: 'Astra',
        label: 'Health & Safety',
        agentType: 'HEALTH_SAFETY',
        image: healthSafetyImg,
        waveImage: healthSafetyWaveImg,
        imagePath: 'patty/guardian/health-safety.svg',
        description:
            'I can assist with any questions you have about Health & Safety.',
        shortDescription:
            'Helps reinforce workplace health and safety, PPE use, and regulatory compliance requirements.',
        fullDescription:
            'Helps reinforce workplace health and safety, PPE use, and regulatory compliance requirements. Astra supports exposure control plans, safe work procedures, hazard prevention strategies, and PPE guidance to help protect staff, residents, and visitors.',
        keywords: ['health', 'safety', 'ppe', 'protective equipment'],
    },
    {
        id: 'education',
        name: 'Mira',
        label: 'Education',
        agentType: 'EDUCATION',
        image: educationImg,
        waveImage: educationWaveImg,
        imagePath: 'patty/guardian/education.svg',
        description:
            'I can assist with any questions you have about Education-related IPAC requirements.',
        shortDescription:
            'Supports education, training, and competency-based learning across IPAC, health and safety, and broader compliance-related topics.',
        fullDescription:
            'Supports education, training, and competency-based learning across IPAC, health and safety, and broader compliance-related topics. Mira helps users navigate onboarding requirements, mandatory training, refreshers, role-specific education, and ongoing learning aligned with regulatory and best-practice standards.',
        keywords: [
            'education',
            'training',
            'learning',
            'course',
            'competency',
            'onboarding',
            'certification',
            'teach',
        ],
    },
    {
        id: 'uti',
        name: 'Nova',
        label: 'UTI',
        agentType: 'UTI',
        image: utiImg,
        waveImage: utiWaveImg,
        imagePath: 'patty/guardian/uti.svg',
        description:
            'I can assist with any questions you have about UTI-related IPAC requirements.',
        shortDescription:
            'Provides targeted guidance on urinary tract infection (UTI) prevention and management.',
        fullDescription:
            'Provides targeted guidance on urinary tract infection (UTI) prevention and management. Nova supports surveillance, documentation, staff education, catheter care practices, and antimicrobial stewardship strategies aligned with IPAC and quality improvement goals.',
        keywords: [
            'uti',
            'urinary tract infection',
            'catheter',
            'foley',
            'bladder',
            'infection',
            'urine',
            'surveillance',
        ],
    },
    {
        id: 'immunization',
        name: 'Echo',
        label: 'Immunization',
        agentType: 'IMMUNIZATION',
        image: immunizationImg,
        waveImage: immunizationWaveImg,
        imagePath: 'patty/guardian/immunization.svg',
        description:
            'I can assist with any questions you have about Immunization-related IPAC requirements.',
        shortDescription:
            'Supports immunization programs and vaccine-related compliance guidance.',
        fullDescription:
            'Supports immunization programs and vaccine-related compliance guidance. Echo assists with immunization schedules, documentation requirements, staff education, outbreak-related vaccination considerations, and alignment with public health and regulatory recommendations.',
        keywords: [
            'immunization',
            'vaccination',
            'vaccine',
            'flu shot',
            'influenza',
            'immunize',
            'inoculation',
            'shot',
        ],
    },
    {
        id: 'data-analysis',
        name: 'Quincy',
        label: 'Data Analysis',
        agentType: 'DATA_ANALYSIS',
        image: dataAnalysisImg,
        waveImage: dataAnalysisWaveImg,
        imagePath: 'patty/guardian/data-analysis.svg',
        description:
            'Supports IPAC, health and safety, and compliance-related data analysis and documentation.',
        shortDescription:
            'Supports IPAC, health and safety, and compliance-related data analysis and documentation.',
        fullDescription:
            'Supports IPAC, health and safety, and compliance-related data analysis and documentation. This Guardian assists with required logs, audits, inspection readiness, retention requirements, and organizing evidence to support regulatory compliance and operational consistency.',
        keywords: [
            'data analysis',
            'analysis',
            'documentation',
            'logs',
            'audit',
            'inspection',
            'retention',
            'evidence',
        ],
    },
    {
        id: 'mifu',
        name: 'Amber',
        label: 'MIFU',
        agentType: 'MIFU',
        image: mifuImg,
        waveImage: mifuWaveImg,
        imagePath: 'patty/guardian/mifu.svg',
        description:
            "Supports teams by providing guidance on manufacturers' instructions for use (MIFUs), helping ensure equipment and products are used, cleaned, and maintained according to required standards.",
        shortDescription:
            "Supports teams by providing guidance on manufacturers' instructions for use (MIFUs), helping ensure equipment and products are used, cleaned, and maintained according to required standards.",
        fullDescription:
            "Supports teams by providing guidance on manufacturers' instructions for use (MIFUs), helping ensure equipment and products are used, cleaned, and maintained according to required standards. Amber helps staff interpret handling, cleaning, maintenance, and use requirements so processes stay aligned with manufacturer guidance and operational expectations.",
        keywords: [
            'mifu',
            'manufacturer instructions for use',
            'instructions for use',
            'manufacturer guidance',
            'equipment',
            'product',
            'cleaning',
            'maintenance',
        ],
    },
    {
        id: 'construction',
        name: 'Atlas',
        label: 'Construction',
        agentType: 'CONSTRUCTION',
        image: constructionImg,
        waveImage: constructionWaveImg,
        imagePath: 'patty/guardian/construction.svg',
        description:
            'I can assist with any questions you have about Construction-related IPAC requirements.',
        shortDescription:
            'Specializes in infection control and compliance considerations for construction, renovation, and maintenance activities.',
        fullDescription:
            'Specializes in infection control and compliance considerations for construction, renovation, and maintenance activities. Atlas provides guidance on containment measures, dust and debris control, air quality considerations, and risk-reduction protocols throughout construction and ICRA-related projects.',
        keywords: ['construction', 'renovation', 'building', 'dust'],
    },
];

/**
 * Get guardian by ID
 * @param {string} id - Guardian ID
 * @returns {Object|undefined} Guardian object or undefined if not found
 */
export const getGuardianById = (id) => {
    if (!id) return undefined;
    return GUARDIANS.find((guardian) => guardian.id === id);
};

/**
 * Get guardian by agent type
 * @param {string} agentType - Agent type (e.g., 'HAND_HYGIENE')
 * @returns {Object|undefined} Guardian object or undefined if not found
 */
export const getGuardianByAgentType = (agentType) => {
    if (!agentType) return undefined;
    return GUARDIANS.find((guardian) => guardian.agentType === agentType);
};

/**
 * Find suggested guardian based on query keywords
 * @param {string} queryText - Query text to match against keywords
 * @param {string} currentAgentType - Current guardian's agent type to exclude
 * @returns {Object} Suggested guardian or Patty as fallback
 */
export const findSuggestedGuardian = (queryText, currentAgentType = null) => {
    // Handle undefined/null queryText
    if (!queryText || typeof queryText !== 'string') {
        return (
            getGuardianByAgentType('PATTY') || {
                id: 'all-topics',
                name: 'Patty',
                label: 'All Topics',
                agentType: 'PATTY',
            }
        );
    }

    const query = queryText.toLowerCase();

    // Find guardian that matches keywords and is NOT the current guardian
    const matchedGuardian = GUARDIANS.find(
        (g) =>
            g.agentType !== currentAgentType &&
            g.keywords &&
            Array.isArray(g.keywords) &&
            g.keywords.some((keyword) => keyword && query.includes(keyword))
    );

    // If found a match, return it. Otherwise return Patty (All Topics) as fallback
    return (
        matchedGuardian ||
        getGuardianByAgentType('PATTY') || {
            id: 'all-topics',
            name: 'Patty',
            label: 'All Topics',
            agentType: 'PATTY',
        }
    );
};
